import { motion } from "framer-motion";
import {
  Search, Clock, Send, CheckCircle2, Eye, MousePointerClick, XCircle,
  AlertTriangle, Cog, User, RefreshCw, ChevronLeft, ChevronRight,
  Copy, Monitor, Smartphone, Mail, ArrowUpDown, Calendar,
} from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { EmailTabNav } from "@/components/admin/email/EmailTabNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { EMAIL_STATUS_CONFIG, EMAIL_CATEGORY_CONFIG } from "@/types/email";
import type { EmailLog, EmailStatus, EmailCategory } from "@/types/email";

const PAGE_SIZE = 25;

const STATUS_ICON_MAP: Record<string, React.ElementType> = {
  Clock, Send, CheckCircle2, Eye, MousePointerClick, XCircle, AlertTriangle,
};

type OriginFilter = "all" | "system" | "admin" | "cron";

function parseOrigin(triggeredBy: string) {
  if (triggeredBy === "system") return { icon: Cog, label: "Sistema", tooltip: "Automático pelo sistema", type: "system" as const };
  if (triggeredBy.startsWith("admin:")) return { icon: User, label: "Admin", tooltip: "Manual por admin", type: "admin" as const };
  if (triggeredBy.startsWith("cron:")) return { icon: Clock, label: "Cron", tooltip: `Job: ${triggeredBy.split(":")[1]}`, type: "cron" as const };
  if (triggeredBy.startsWith("retry:")) return { icon: RefreshCw, label: "Reenvio", tooltip: `Reenvio do #${triggeredBy.split(":")[1].slice(0, 8)}`, type: "admin" as const };
  return { icon: Cog, label: triggeredBy, tooltip: triggeredBy, type: "system" as const };
}

const ALL_STATUSES: EmailStatus[] = ["pending", "sent", "delivered", "opened", "clicked", "failed", "bounced"];

export default function AdminEmailLog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Filters
  const [searchTerm, setSearchTerm] = useState(searchParams.get("q") || "");
  const [activeStatuses, setActiveStatuses] = useState<Set<EmailStatus>>(() => {
    const s = searchParams.get("status");
    return s ? new Set(s.split(",") as EmailStatus[]) : new Set<EmailStatus>();
  });
  const [templateFilter, setTemplateFilter] = useState(searchParams.get("template") || "");
  const [originFilter, setOriginFilter] = useState<OriginFilter>("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [quickPeriod, setQuickPeriod] = useState<"today" | "7d" | "14d" | "28d" | "30d" | "90d" | "all" | "custom">("all");
  const [sortAsc, setSortAsc] = useState(false);
  const [page, setPage] = useState(Number(searchParams.get("page")) || 0);

  // Sync filters to URL params (EM03 AC7)
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set("q", searchTerm);
    if (activeStatuses.size > 0) params.set("status", Array.from(activeStatuses).join(","));
    if (templateFilter) params.set("template", templateFilter);
    if (originFilter !== "all") params.set("origin", originFilter);
    if (dateFrom) params.set("from", dateFrom);
    if (dateTo) params.set("to", dateTo);
    if (page > 0) params.set("page", String(page));
    setSearchParams(params, { replace: true });
  }, [searchTerm, activeStatuses, templateFilter, originFilter, dateFrom, dateTo, page]);

  // Detail + Resend dialogs
  const [selectedLog, setSelectedLog] = useState<EmailLog | null>(null);
  const [previewMode, setPreviewMode] = useState<"desktop" | "mobile">("desktop");
  const [resendLog, setResendLog] = useState<EmailLog | null>(null);
  const [resendDiffEmail, setResendDiffEmail] = useState(false);
  const [resendEmail, setResendEmail] = useState("");
  const [resendConfirmed, setResendConfirmed] = useState(false);

  // Template names + categories for dropdown
  const { data: templatesData } = useQuery({
    queryKey: ["email-template-names-full"],
    queryFn: async () => {
      const { data } = await supabase.from("email_templates_config").select("template_key, display_name, category");
      return data ?? [];
    },
    staleTime: 10 * 60 * 1000,
  });

  const templateNames = Object.fromEntries((templatesData ?? []).map((t) => [t.template_key, t.display_name]));
  const templatesByCategory = (templatesData ?? []).reduce((acc, t) => {
    const cat = t.category as EmailCategory;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(t);
    return acc;
  }, {} as Record<EmailCategory, typeof templatesData>);

  // Main query
  const { data, isLoading } = useQuery({
    queryKey: ["email-logs", searchTerm, Array.from(activeStatuses), templateFilter, originFilter, dateFrom, dateTo, sortAsc, page],
    queryFn: async () => {
      let query = supabase
        .from("email_logs")
        .select("*", { count: "exact" })
        .order("created_at", { ascending: sortAsc })
        .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

      if (searchTerm) {
        query = query.or(`recipient_email.ilike.%${searchTerm}%,recipient_name.ilike.%${searchTerm}%`);
      }
      if (activeStatuses.size > 0) {
        query = query.in("status", Array.from(activeStatuses));
      }
      if (templateFilter) {
        query = query.eq("template_key", templateFilter);
      }
      if (originFilter !== "all") {
        query = query.like("triggered_by", originFilter === "system" ? "system" : `${originFilter}:%`);
      }
      if (dateFrom) {
        query = query.gte("created_at", `${dateFrom}T00:00:00`);
      }
      if (dateTo) {
        query = query.lte("created_at", `${dateTo}T23:59:59`);
      }

      const { data, error, count } = await query;
      if (error) throw error;
      return { logs: (data ?? []) as EmailLog[], total: count ?? 0 };
    },
  });

  const totalPages = Math.ceil((data?.total ?? 0) / PAGE_SIZE);

  // Admin names for origin display (EM17 AC3) — must be after `data` declaration
  const adminIds = [...new Set(
    (data?.logs ?? [])
      .map((l) => l.triggered_by)
      .filter((t) => t.startsWith("admin:") || t.startsWith("retry:"))
      .map((t) => t.split(":")[1])
      .filter(Boolean)
  )];

  const { data: adminProfiles } = useQuery({
    queryKey: ["admin-profiles-for-log", adminIds.join(",")],
    queryFn: async () => {
      if (adminIds.length === 0) return {};
      const { data: profiles } = await supabase.from("profiles").select("id, full_name").in("id", adminIds);
      return Object.fromEntries((profiles ?? []).map((p) => [p.id, p.full_name || "Admin"]));
    },
    enabled: adminIds.length > 0,
  });

  function getOriginLabel(triggeredBy: string): string {
    if (triggeredBy === "system") return "Sistema";
    if (triggeredBy.startsWith("admin:")) {
      const uid = triggeredBy.split(":")[1];
      return adminProfiles?.[uid] || "Admin";
    }
    if (triggeredBy.startsWith("cron:")) return `Cron: ${triggeredBy.split(":")[1]}`;
    if (triggeredBy.startsWith("retry:")) {
      const uid = triggeredBy.split(":")[1];
      return `Reenvio por ${adminProfiles?.[uid] || "admin"}`;
    }
    return triggeredBy;
  }

  function toggleStatus(status: EmailStatus) {
    setActiveStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      setPage(0);
      return next;
    });
  }

  function clearAllFilters() {
    setSearchTerm("");
    setActiveStatuses(new Set());
    setTemplateFilter("");
    setOriginFilter("all");
    setDateFrom("");
    setDateTo("");
    setQuickPeriod("all");
    setPage(0);
  }

  const hasFilters = searchTerm || activeStatuses.size > 0 || templateFilter || originFilter !== "all" || dateFrom || dateTo;

  // Resend mutation — includes retry_of + original_status metadata
  const resendMutation = useMutation({
    mutationFn: async (log: EmailLog) => {
      const targetEmail = resendDiffEmail && resendEmail ? resendEmail : log.recipient_email;
      const { data, error } = await supabase.functions.invoke("send-manual-email", {
        body: {
          templateKey: log.template_key,
          recipientEmail: targetEmail,
          recipientName: log.recipient_name || targetEmail,
          recipientType: log.recipient_type,
          variables: {},
          htmlBodyOverride: log.html_body,
          subject: log.subject,
          metadata: {
            retry_of: log.id,
            original_status: log.status,
            original_email: log.recipient_email,
            override_email: resendDiffEmail ? resendEmail : undefined,
          },
        },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({ title: "Email reenviado com sucesso" });
      setResendLog(null);
      setResendConfirmed(false);
      setResendDiffEmail(false);
      setResendEmail("");
      queryClient.invalidateQueries({ queryKey: ["email-logs"] });
    },
    onError: (err) => {
      toast({ title: "Erro ao reenviar", description: String(err), variant: "destructive" });
    },
  });

  return (
    <div className="p-6 lg:p-8 max-w-7xl">
      <EmailTabNav />

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Log de Envios</h1>
          <p className="text-sm text-muted-foreground">Histórico completo de todos os emails enviados</p>
        </div>
        {data && (
          <p className="text-sm text-muted-foreground">{data.total.toLocaleString("pt-BR")} emails encontrados</p>
        )}
      </div>

      {/* Filters */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-3 mb-6">
        {/* Row 1: search + template dropdown + date range */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar email ou nome..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
              className="pl-10"
            />
          </div>

          {/* Template dropdown agrupado por categoria */}
          <select
            value={templateFilter}
            onChange={(e) => { setTemplateFilter(e.target.value); setPage(0); }}
            className="h-10 rounded-xl border border-border bg-background px-3 text-sm w-60"
          >
            <option value="">Todos os templates</option>
            {Object.entries(templatesByCategory).map(([cat, items]) => (
              <optgroup key={cat} label={EMAIL_CATEGORY_CONFIG[cat as EmailCategory]?.label || cat}>
                {(items ?? []).map((t) => (
                  <option key={t.template_key} value={t.template_key}>{t.display_name}</option>
                ))}
              </optgroup>
            ))}
          </select>

          {/* Date range inputs */}
          <div className="flex items-center gap-1.5">
            <Calendar className="w-4 h-4 text-muted-foreground" />
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setQuickPeriod("custom"); setPage(0); }}
              className="h-10 rounded-xl border border-border bg-background px-3 text-sm w-36"
              placeholder="De"
            />
            <span className="text-muted-foreground text-xs">até</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setQuickPeriod("custom"); setPage(0); }}
              className="h-10 rounded-xl border border-border bg-background px-3 text-sm w-36"
              placeholder="Até"
            />
          </div>
        </div>

        {/* Row 1.5: Quick period chips */}
        <div className="flex flex-wrap gap-1.5">
          {([
            { value: "today", label: "Hoje" },
            { value: "7d", label: "7 dias" },
            { value: "14d", label: "14 dias" },
            { value: "28d", label: "28 dias" },
            { value: "30d", label: "30 dias" },
            { value: "90d", label: "90 dias" },
            { value: "all", label: "Todo o tempo" },
          ] as const).map((p) => (
            <button
              key={p.value}
              onClick={() => {
                setQuickPeriod(p.value);
                const today = new Date().toISOString().slice(0, 10);
                if (p.value === "all") {
                  setDateFrom(""); setDateTo("");
                } else if (p.value === "today") {
                  setDateFrom(today); setDateTo(today);
                } else {
                  const days = parseInt(p.value);
                  const from = new Date(Date.now() - days * 86400000).toISOString().slice(0, 10);
                  setDateFrom(from); setDateTo(today);
                }
                setPage(0);
              }}
              className={`text-[11px] font-medium px-2.5 py-1 rounded-full transition-colors ${
                quickPeriod === p.value
                  ? "bg-primary text-primary-foreground"
                  : "bg-secondary text-secondary-foreground border border-border hover:bg-muted"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Row 2: status chips + origem chips + clear */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Status chips */}
          <div className="flex flex-wrap gap-1.5">
            {ALL_STATUSES.map((status) => {
              const config = EMAIL_STATUS_CONFIG[status];
              const isActive = activeStatuses.has(status);
              return (
                <button
                  key={status}
                  onClick={() => toggleStatus(status)}
                  className={`text-[11px] font-medium px-2.5 py-1 rounded-full transition-colors ${
                    isActive ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground border border-border hover:bg-muted"
                  }`}
                >
                  {config.label}
                </button>
              );
            })}
          </div>

          <div className="h-4 w-px bg-border" />

          {/* Origem chips */}
          <div className="flex gap-1.5">
            {([
              { value: "all" as OriginFilter, label: "Todas origens" },
              { value: "system" as OriginFilter, label: "Sistema" },
              { value: "admin" as OriginFilter, label: "Admin" },
              { value: "cron" as OriginFilter, label: "Cron" },
            ]).map((o) => (
              <button
                key={o.value}
                onClick={() => { setOriginFilter(o.value); setPage(0); }}
                className={`text-[11px] font-medium px-2.5 py-1 rounded-full transition-colors ${
                  originFilter === o.value ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground border border-border hover:bg-muted"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>

          {hasFilters && (
            <button onClick={clearAllFilters} className="text-[11px] font-medium px-2.5 py-1 rounded-full text-muted-foreground hover:text-foreground">
              Limpar tudo
            </button>
          )}
        </div>
      </motion.div>

      {/* Table */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-card rounded-2xl border border-border overflow-hidden">
        {isLoading ? (
          <div className="p-8 space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-12 bg-muted/50 rounded animate-pulse" />
            ))}
          </div>
        ) : data && data.logs.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-muted/50">
                  <tr className="text-xs font-medium text-muted-foreground uppercase">
                    <th className="px-4 py-3 cursor-pointer select-none hover:text-foreground" onClick={() => setSortAsc(!sortAsc)}>
                      <span className="flex items-center gap-1">Data/hora <ArrowUpDown className="w-3 h-3" /></span>
                    </th>
                    <th className="px-4 py-3">Template</th>
                    <th className="px-4 py-3">Destinatário</th>
                    <th className="px-4 py-3">Assunto</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Origem</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.logs.map((log) => {
                    const statusConfig = EMAIL_STATUS_CONFIG[log.status as EmailStatus];
                    const IconComp = STATUS_ICON_MAP[statusConfig?.icon] || Mail;
                    const origin = parseOrigin(log.triggered_by);
                    return (
                      <tr key={log.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                          {new Date(log.created_at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                        </td>
                        <td className="px-4 py-3 text-sm">{templateNames[log.template_key] ?? log.template_key}</td>
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium truncate max-w-[180px]">{log.recipient_name || "—"}</p>
                          <p className="text-xs text-muted-foreground truncate max-w-[180px]">{log.recipient_email}</p>
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground truncate max-w-[200px]">{log.subject}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-full ${statusConfig?.color}`}>
                            <IconComp className="w-3.5 h-3.5" /> {statusConfig?.label}
                          </span>
                        </td>
                        <td className="px-4 py-3" title={origin.tooltip}>
                          <div className="flex items-center gap-1.5">
                            <origin.icon className="w-4 h-4 text-muted-foreground shrink-0" />
                            <span className="text-xs text-muted-foreground truncate max-w-[100px]">
                              {getOriginLabel(log.triggered_by)}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Button variant="ghost" size="sm" onClick={() => setSelectedLog(log)}>Detalhes</Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-4 py-3 border-t border-border flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                Mostrando <span className="font-bold text-foreground">{page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, data.total)}</span> de{" "}
                <span className="font-bold text-foreground">{data.total.toLocaleString("pt-BR")}</span>
              </p>
              <div className="flex gap-1">
                <Button variant="outline" size="icon-sm" disabled={page === 0} onClick={() => setPage(page - 1)}><ChevronLeft className="w-4 h-4" /></Button>
                <Button variant="outline" size="icon-sm" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}><ChevronRight className="w-4 h-4" /></Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
            <Search className="w-7 h-7 mb-3" />
            <p className="font-medium">Nenhum email encontrado</p>
            <p className="text-xs mb-4">Tente ajustar os filtros ou buscar por outro termo</p>
            {hasFilters && <Button variant="outline" size="sm" onClick={clearAllFilters}>Limpar filtros</Button>}
          </div>
        )}
      </motion.div>

      {/* ─── Dialog: Detalhes do Email ─── */}
      <Dialog open={!!selectedLog} onOpenChange={(open) => !open && setSelectedLog(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 py-4 border-b border-border">
            <DialogTitle>Detalhes do Email</DialogTitle>
          </DialogHeader>
          {selectedLog && (
            <div className="flex-1 flex overflow-hidden">
              {/* Left: Metadata 40% */}
              <div className="w-[40%] border-r border-border overflow-y-auto p-6 space-y-6">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className={`inline-flex items-center gap-1 text-xs font-medium px-3 py-1 rounded-full ${EMAIL_STATUS_CONFIG[selectedLog.status as EmailStatus]?.color}`}>
                      {EMAIL_STATUS_CONFIG[selectedLog.status as EmailStatus]?.label}
                    </span>
                    <code className="text-[10px] text-muted-foreground bg-muted px-2 py-1 rounded">{selectedLog.resend_message_id || "—"}</code>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase text-muted-foreground">Template</p>
                    <p className="text-sm font-semibold">{templateNames[selectedLog.template_key] ?? selectedLog.template_key}</p>
                    <p className="text-xs text-primary font-mono">{selectedLog.template_key}</p>
                  </div>
                </div>

                <div className="bg-muted/50 p-4 rounded-xl space-y-3">
                  <div>
                    <p className="text-[10px] font-bold uppercase text-muted-foreground">Assunto</p>
                    <p className="text-sm font-semibold">{selectedLog.subject}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase text-muted-foreground">Destinatário</p>
                    <p className="text-sm font-bold">{selectedLog.recipient_name || "—"}</p>
                    <p className="text-xs text-muted-foreground">{selectedLog.recipient_email}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase text-muted-foreground">Origem</p>
                    <p className="text-sm">{getOriginLabel(selectedLog.triggered_by)}</p>
                  </div>
                </div>

                {/* Timeline */}
                <div className="space-y-2">
                  <p className="text-[10px] font-bold uppercase text-muted-foreground">Rastreamento</p>
                  <div className="relative ml-2 space-y-0">
                    <div className="absolute left-[7px] top-2 bottom-2 w-[2px] bg-border" />
                    {[
                      { label: "Criado", ts: selectedLog.created_at },
                      { label: "Enviado", ts: selectedLog.sent_at },
                      { label: "Entregue", ts: selectedLog.delivered_at },
                      { label: "Aberto", ts: selectedLog.opened_at },
                      { label: "Clicado", ts: selectedLog.clicked_at },
                    ].map((step) => (
                      <div key={step.label} className="relative flex gap-4 pb-3 items-start">
                        <div className={`mt-1.5 w-4 h-4 rounded-full border-4 border-card z-10 ${step.ts ? "bg-primary" : "bg-muted"}`} />
                        <div>
                          <p className={`text-xs font-bold ${step.ts ? "text-foreground" : "text-muted-foreground"}`}>{step.label}</p>
                          <p className="text-[11px] text-muted-foreground">{step.ts ? new Date(step.ts).toLocaleString("pt-BR") : "Pendente"}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {selectedLog.status === "failed" && selectedLog.error_message && (
                  <div className="bg-red-50 border border-red-200 p-4 rounded-xl">
                    <p className="text-xs font-bold text-red-700 mb-1">Erro</p>
                    <p className="text-sm text-red-800">{selectedLog.error_message}</p>
                    {selectedLog.error_code && <code className="text-[10px] text-red-600 mt-1 block">{selectedLog.error_code}</code>}
                  </div>
                )}

                {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                  <details className="group">
                    <summary className="text-xs font-bold text-foreground cursor-pointer p-3 rounded-xl bg-muted/50 hover:bg-muted">Metadata JSON</summary>
                    <pre className="mt-2 bg-foreground text-background text-[10px] p-4 rounded-xl overflow-auto font-mono">{JSON.stringify(selectedLog.metadata, null, 2)}</pre>
                  </details>
                )}
              </div>

              {/* Right: Preview 60% */}
              <div className="w-[60%] flex flex-col bg-muted/20">
                <div className="px-4 py-3 border-b border-border flex items-center justify-between bg-card">
                  <span className="text-xs font-bold uppercase text-muted-foreground">Preview</span>
                  <div className="flex bg-muted p-1 rounded-lg gap-1">
                    <button onClick={() => setPreviewMode("desktop")} className={`p-1.5 rounded-md text-xs flex items-center gap-1 ${previewMode === "desktop" ? "bg-card shadow-sm text-primary" : "text-muted-foreground"}`}>
                      <Monitor className="w-3.5 h-3.5" /> Desktop
                    </button>
                    <button onClick={() => setPreviewMode("mobile")} className={`p-1.5 rounded-md text-xs flex items-center gap-1 ${previewMode === "mobile" ? "bg-card shadow-sm text-primary" : "text-muted-foreground"}`}>
                      <Smartphone className="w-3.5 h-3.5" /> Mobile
                    </button>
                  </div>
                </div>
                <div className="flex-1 p-6 overflow-auto flex justify-center">
                  {selectedLog.html_body ? (
                    <iframe srcDoc={selectedLog.html_body} sandbox="allow-same-origin" className="bg-white shadow-lg rounded-sm border border-border" style={{ width: previewMode === "desktop" ? 600 : 375, minHeight: 500 }} title="Email preview" />
                  ) : (
                    <div className="flex flex-col items-center justify-center text-muted-foreground">
                      <Mail className="w-7 h-7 mb-3" />
                      <p className="font-medium">Preview não disponível</p>
                      <p className="text-xs">Este email não possui HTML armazenado</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="px-6 py-4 border-t border-border flex gap-2">
            {selectedLog?.html_body && (
              <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(selectedLog.html_body!); toast({ title: "HTML copiado" }); }}>
                <Copy className="w-4 h-4 mr-2" /> Copiar HTML
              </Button>
            )}
            <Button size="sm" onClick={() => { setResendLog(selectedLog); setSelectedLog(null); }}>
              <Send className="w-4 h-4 mr-2" /> Reenviar
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setSelectedLog(null)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Dialog: Reenviar Email ─── */}
      <Dialog open={!!resendLog} onOpenChange={(open) => { if (!open) { setResendLog(null); setResendConfirmed(false); setResendDiffEmail(false); setResendEmail(""); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Reenviar email</DialogTitle>
            <p className="text-sm text-muted-foreground">Revise os detalhes antes de processar o novo disparo.</p>
          </DialogHeader>
          {resendLog && (
            <div className="space-y-4">
              <div className="bg-muted/50 p-4 rounded-xl space-y-2">
                <div><p className="text-[10px] font-bold uppercase text-muted-foreground">Destinatário</p><p className="text-sm font-semibold">{resendLog.recipient_email}</p></div>
                <div><p className="text-[10px] font-bold uppercase text-muted-foreground">Template</p><p className="text-sm font-semibold">{templateNames[resendLog.template_key] ?? resendLog.template_key}</p></div>
                <div><p className="text-[10px] font-bold uppercase text-muted-foreground">Assunto</p><p className="text-sm font-semibold">{resendLog.subject}</p></div>
              </div>
              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <Checkbox checked={resendDiffEmail} onCheckedChange={(v) => setResendDiffEmail(!!v)} />
                  <span className="text-sm font-medium">Enviar para email diferente</span>
                </label>
                {resendDiffEmail && <Input type="email" placeholder="Digite o novo endereço de email" value={resendEmail} onChange={(e) => setResendEmail(e.target.value)} />}
              </div>
              <label className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl cursor-pointer">
                <Checkbox checked={resendConfirmed} onCheckedChange={(v) => setResendConfirmed(!!v)} className="mt-0.5" />
                <span className="text-sm font-bold text-amber-800">Confirmo que quero reenviar este email</span>
              </label>
              <div className="flex items-start gap-2 px-1">
                <AlertTriangle className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                <p className="text-xs text-muted-foreground">Será criado um novo registro no log. <strong>O email original não será alterado.</strong></p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setResendLog(null)}>Cancelar</Button>
            <Button disabled={!resendConfirmed || resendMutation.isPending || (resendDiffEmail && !resendEmail)} onClick={() => resendLog && resendMutation.mutate(resendLog)}>
              <Send className="w-4 h-4 mr-2" /> {resendMutation.isPending ? "Reenviando..." : "Reenviar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
